import React, { createContext, useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import axios from '../services/AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';
import { useSettings } from './SettingsContext';

/**
 * CapabilityContext — Phase 1.2 of the access-control redesign.
 *
 * Server contract: GET /:companyId/me/capabilities (see backend
 * me/services/capabilitiesService.js). Response shape from
 * docs/ACCESS_CONTROL_REDESIGN.md §2.3:
 *
 *   {
 *     userId, companyId, employeeId,
 *     roleNames: string[],
 *     isAdmin, isOwner, isPayrollAdmin, isHrAdmin,
 *     managesEmployeeIds: string[],
 *     scopes: {
 *       time:    { view, approve, report },
 *       expense: { view, approve },
 *     },
 *     managedProjectIds: string[]   // populated for users that hold
 *                                   //   project_member.project_role='manager'
 *                                   //   on one or more projects; consumed by
 *                                   //   ProjectDetailsPage.canManage gate
 *                                   //   per feedback_project_authoritative_manager.md
 *   }
 *
 * Caching: the backend already caches the response 60s per (user, company);
 * the client fetches once per companyId change and keeps the result until
 * the next change OR until `refreshCapabilities()` is called explicitly
 * (used after a role-update PUT, after company-switch, after invitation
 * accept). Mirrors how FeatureContext handles entitlements.
 *
 * Fail-closed default: while loading or on fetch error, every boolean is
 * false and every scope is 'none'. CapabilityRoute (Phase 1.3) gates on
 * `hasLoaded` first to avoid a flash of the 403 page during initial fetch.
 *
 * Migration breadcrumbs:
 *   - AuthProvider.jsx writes auth.user.companies[cId].roles into Redux at
 *     login. That field is unread by any component (verified in the v2
 *     redesign review) and is now marked DEPRECATED. Any new caller-self
 *     role check should go through useCapabilities() instead.
 *   - Settings/UserSettings.js, Settings/CompanySettings.jsx, and
 *     Settings/UserRoleModal.js still fetch user/role data on demand for
 *     OTHER users (multi-user list views). For caller-SELF questions
 *     ("am I the Owner of this company?") prefer this hook.
 */

// Scope ordering (matches backend's deriveScopes monotonicity):
//   'all' > 'team' > 'own' > 'none'
// hasScope('time', 'view', 'team') returns true iff granted is 'all' or 'team'.
//
// The project domain (§4.4) uses its own two words for the middle ranks —
// 'member' (you belong to ≥1 project) and 'managed' (you manage ≥1) — which
// capabilitiesService emits for project.view / project.manage. They must be
// ranked here or the lookup yields undefined, the comparison becomes
// `NaN >= n`, and every project gate silently fails closed regardless of what
// the server granted.
const SCOPE_RANK = {
    none: 0,
    own: 1,
    member: 1,
    team: 2,
    managed: 2,
    all: 3,
};

const defaultCapabilities = {
    userId: null,
    companyId: null,
    employeeId: null,
    roleNames: [],
    isAdmin: false,
    isOwner: false,
    isPayrollAdmin: false,
    isHrAdmin: false,
    managesEmployeeIds: [],
    // `managedProjectIds`: project IDs where the caller holds
    // project_member.project_role='manager'. Consumed by ProjectDetailsPage
    // for the `canManage` gate. Per feedback_project_authoritative_manager.md
    // every authz helper must agree on this source-of-truth.
    // Backend source: projects/utils/authorize.getManagedProjectIds.
    managedProjectIds: [],
    // `isProjectMember`: caller is a project_member on at least one project
    // in this company. Used by the projects landing page to show "your
    // projects" vs an empty state. Backend source:
    // me/services/capabilitiesService — derived from project_member table.
    isProjectMember: false,
    scopes: {
        // `report: 'none'` was added 2026-05-14 (post-master-merge review).
        // SideBar (`hasScope('time','report','all')`) and
        // <CapabilityRoute capability="time.report.all"> both consume this
        // scope; before the addition it lived only in the backend response,
        // so the fail-closed branch returned undefined → SCOPE_RANK[undefined]
        // → NaN >= 3 → false (worked by accident). Making it explicit
        // documents the contract.
        time: { view: 'none', approve: 'none', report: 'none' },
        expense: { view: 'none', approve: 'none' },
        // Present for the same reason as the two above: without it,
        // `scopes.project?.create` is undefined while capabilities load, and
        // the fail-closed default is implicit rather than stated.
        project: { view: 'none', manage: 'none', create: 'none' },
    },
};

const CapabilityContext = createContext({
    capabilities: defaultCapabilities,
    isLoading: false,
    hasLoaded: false,
    error: null,
    refreshCapabilities: () => { },
    hasScope: () => false,
});

export const CapabilityProvider = ({ children }) => {
    const { companyId, registerRefreshFunction } = useSettings();
    const [capabilities, setCapabilities] = useState(defaultCapabilities);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [error, setError] = useState(null);
    const mountedRef = useRef(true);
    const companyRef = useRef(companyId);
    companyRef.current = companyId;

    const fetchCapabilities = useCallback(async () => {
        if (!companyId) {
            // Reset between company switches so a stale capability set never
            // leaks into the wrong tenant. defaultCapabilities is fail-closed.
            setCapabilities(defaultCapabilities);
            setHasLoaded(false);
            setError(null);
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            // Phase 1.6 telemetry — every refresh emits a structured log
            // line matching the backend [event-name] convention. High rates
            // indicate cache invalidation churn or a refresh-loop bug; low
            // rates with TTL ≤60s server-side mean caching is effective.
            // eslint-disable-next-line no-console
            console.info(
              `[capability-context-refresh] companyId=${companyId}`,
            );

            const url = getApiUrl(`${companyId}/me/capabilities`);
            const { data } = await axios.get(url, { withCredentials: true });

            if (!mountedRef.current || companyRef.current !== companyId) return;

            // Defensive normalization — if the server response is missing a
            // sub-tree, fall back to the fail-closed defaults rather than
            // crash a downstream `scopes.time.view` access.
            // Merge per-domain against the fail-closed defaults, but derive
            // the domain list from the defaults rather than naming each one.
            // The previous version hard-coded `time` and `expense`, so when
            // the backend added `project` (§4.4) the whole sub-tree was
            // silently dropped: the server sent
            // project:{view:'all',manage:'all',create:'all'} and every
            // project gate still read `undefined` and failed closed. Adding a
            // domain server-side must not require an edit here.
            const mergedScopes = {};
            const domains = new Set([
                ...Object.keys(defaultCapabilities.scopes),
                ...Object.keys(data?.scopes || {}),
            ]);
            domains.forEach((domain) => {
                mergedScopes[domain] = {
                    ...(defaultCapabilities.scopes[domain] || {}),
                    ...(data?.scopes?.[domain] || {}),
                };
            });

            setCapabilities({
                ...defaultCapabilities,
                ...data,
                scopes: mergedScopes,
            });
        } catch (err) {
            if (mountedRef.current && companyRef.current === companyId) {
                console.error('Error fetching capabilities:', err);
                // Keep fail-closed defaults so consumers don't observe a
                // half-populated response on error.
                setCapabilities(defaultCapabilities);
                setError(err);
            }
        } finally {
            if (mountedRef.current && companyRef.current === companyId) {
                setIsLoading(false);
                setHasLoaded(true);
            }
        }
    }, [companyId]);

    useEffect(() => {
        mountedRef.current = true;
        setHasLoaded(false);
        fetchCapabilities();

        if (registerRefreshFunction) {
            const unregister = registerRefreshFunction('capabilities', fetchCapabilities);
            return () => {
                mountedRef.current = false;
                if (unregister) unregister();
            };
        }

        return () => {
            mountedRef.current = false;
        };
    }, [companyId, fetchCapabilities, registerRefreshFunction]);

    /**
     * hasScope('time', 'view', 'team') → true iff the granted scope ≥ 'team'.
     * Useful for UI affordances: render the *Mitt team* tab only when
     * hasScope('time', 'view', 'team').
     */
    const hasScope = useCallback((module, action, requiredLevel) => {
        const granted = capabilities?.scopes?.[module]?.[action] ?? 'none';
        return SCOPE_RANK[granted] >= SCOPE_RANK[requiredLevel];
    }, [capabilities]);

    const value = useMemo(() => ({
        capabilities,
        isLoading,
        hasLoaded,
        error,
        refreshCapabilities: fetchCapabilities,
        hasScope,
    }), [capabilities, isLoading, hasLoaded, error, fetchCapabilities, hasScope]);

    return (
        <CapabilityContext.Provider value={value}>
            {children}
        </CapabilityContext.Provider>
    );
};

/**
 * Read-only access to the caller's capabilities. Returns the fail-closed
 * default object until the initial fetch completes — gate on `hasLoaded`
 * before making routing decisions.
 *
 * Common patterns:
 *   const { capabilities, hasScope } = useCapabilities();
 *   if (capabilities.isOwner) ...
 *   if (hasScope('time', 'approve', 'team')) ...
 */
export const useCapabilities = () => useContext(CapabilityContext);

export default CapabilityContext;
